import React, { useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import {
  Mail,
  MailOpen,
  Archive,
  Trash2,
  Check,
  Ban,
  Loader2,
  Paperclip,
  Clock,
  Reply,
  ReplyAll,
  Forward,
  CheckSquare,
  Tag,
  Flame,
  ShieldCheck,
  FolderInput,
  UserRound,
} from 'lucide-react';
import { AutoLabelBadge } from '@/components/AutoLabelBadge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { RichTooltip, RichTooltipContent, RichTooltipTrigger } from '@/components/ui/rich-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TrackingIndicator } from '@/components/TrackingIndicator';
import { cn } from '@/lib/utils';
import { getDateTimeDisplay } from '@/lib/dateDisplay';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { createEmailDragPreview } from '@/lib/dragPreview';
import type { Email } from '@/types/email';
import { useMessageTracking } from '@/hooks/useEmailTracking';
import { formatAddressDisplay, formatAddressList, getInitials } from '../utils/emailFormatting';
import { useHoverStore } from '@/stores/hoverStore';
import { useEmailStore } from '@/stores/emailStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { useSenderProfileStore } from '@/stores/senderProfileStore';
import { useUIStore } from '@/stores/uiStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useLongPress } from '@/hooks/useLongPress';
import { useIsMobile, useIsTouch } from '@/hooks/useIsMobile';
import { SwipeableRow, type SwipeActionConfig, type SwipeableRowHandle } from './SwipeableRow';
import { RowHoverActions, HoverActionButton } from './RowHoverActions';
import { motion } from 'motion/react';
import { easings } from '@/lib/animations';

const MAX_STAGGERED_ANIMATION_ITEMS = 0;
const STAGGER_STEP_SECONDS = 0.03;

export interface EmailListItemProps {
  email: Email;
  index: number;
  isSelected: boolean;
  isChecked: boolean;
  showCheckbox: boolean;
  accountColor: string | null | undefined;
  showAccountColors: boolean;
  operationState?: 'pending' | 'error';
  draggingEmailId: string | null;
  disableDrag?: boolean;
  onEmailSelect: (emailId: string, folderId?: string | null, accountId?: string | null, threadId?: string | null) => void;
  onToggleBulkSelect: (emailId: string) => void;
  onEmailMarkRead: (emailId: string) => void;
  onEmailMarkUnread: (emailId: string) => void;
  onEmailArchive: (emailId: string) => void;
  onEmailDelete: (emailId: string) => void;
  onEmailApproveSender: (emailId: string) => void;
  onEmailBlockSender: (emailId: string) => void;
  onEmailReply?: (emailId: string) => void;
  onEmailReplyAll?: (emailId: string) => void;
  onEmailForward?: (emailId: string) => void;
  onCreateTask?: (emailId: string, anchor?: HTMLElement | null) => void;
  onAddSenderLabel?: (emailId: string) => void;
  onTrainSpam?: (emailId: string) => void;
  onTrainGood?: (emailId: string) => void;
  onRemoveLabel?: (emailId: string, labelId: string) => void;
  onEmailDragStart?: (email: Email) => void;
  onEmailDragEnd?: () => void;
  showRecipientNames?: boolean;
  contactNameLookup?: ReadonlyMap<string, string>;
}

export const EmailListItem = React.memo(function EmailListItem({
  email,
  index,
  isSelected,
  isChecked,
  showCheckbox,
  accountColor,
  showAccountColors,
  operationState,
  draggingEmailId,
  disableDrag = false,
  onEmailSelect,
  onToggleBulkSelect,
  onEmailMarkRead,
  onEmailMarkUnread,
  onEmailArchive,
  onEmailDelete,
  onEmailApproveSender,
  onEmailBlockSender,
  onEmailReply,
  onEmailReplyAll,
  onEmailForward,
  onCreateTask,
  onAddSenderLabel,
  onTrainSpam,
  onTrainGood,
  onRemoveLabel,
  onEmailDragStart,
  onEmailDragEnd,
  showRecipientNames = false,
  contactNameLookup,
}: EmailListItemProps) {
  const setHoveredEmail = useHoverStore((state) => state.setHoveredEmail);
  const clearHover = useHoverStore((state) => state.clearHover);
  const labelsVisible = useUIStore((state) => state.labelsVisible);
  const setBulkEditMode = useBulkOperationsStore((state) => state.setBulkEditMode);
  const swipeRowRef = useRef<SwipeableRowHandle>(null);
  const swipeDisabled = showCheckbox;

  // Round 4: selection replaces the bulk-mode toggle button. Long-press
  // (touch) enters bulk mode with this row selected; on desktop a checkbox
  // surfaces over the avatar on hover.
  const enterBulkSelect = useCallback(() => {
    setBulkEditMode(true);
    if (!isChecked) {
      onToggleBulkSelect(email.id);
    }
  }, [setBulkEditMode, isChecked, onToggleBulkSelect, email.id]);
  const longPressHandlers = useLongPress(enterBulkSelect, { disabled: showCheckbox });

  // Track read receipts for sent emails only
  // Use showRecipientNames (which is true for Sent folder) to detect sent emails
  const isSent = showRecipientNames && !email.draft;
  const trackingEnabled = isSent;
  const { tracking, isLoading: trackingLoading } = useMessageTracking(trackingEnabled ? email.id : null);

  // Memoize only expensive calculations
  const primaryRecipient = Array.isArray(email.to) && email.to.length > 0 ? email.to[0] : null;
  const showRecipientContext = Boolean(showRecipientNames && primaryRecipient);
  const displayAddress = showRecipientContext && primaryRecipient ? primaryRecipient : email.from;
  const normalizedRecipientEmail =
    showRecipientContext && displayAddress?.email ? displayAddress.email.trim().toLowerCase() : null;
  const contactRecipientName =
    normalizedRecipientEmail && contactNameLookup
      ? contactNameLookup.get(normalizedRecipientEmail) ?? null
      : null;
  const baseDisplayName =
    displayAddress?.name?.trim() ||
    displayAddress?.email ||
    email.from.name ||
    email.from.email ||
    'Unknown sender';
  const effectiveDisplayName =
    showRecipientContext && contactRecipientName ? contactRecipientName : baseDisplayName;
  const displayLabel = showRecipientContext ? `To: ${effectiveDisplayName}` : baseDisplayName;
  const avatarAltLabel =
    showRecipientContext && effectiveDisplayName
      ? effectiveDisplayName
      : email.from.name || email.from.email || 'Sender avatar';
  const avatarEmail = displayAddress?.email || email.from.email;
  
  // Expensive: URL generation
  const avatarSrc = useMemo(
    () => (avatarEmail ? getBrandfetchLogoUrl(avatarEmail) : null),
    [avatarEmail]
  );
  
  // Expensive: Date parsing and formatting
  const { shortLabel: formattedDate, fullLabel: fullDate } = useMemo(
    () => getDateTimeDisplay(email.date),
    [email.date],
  );
  
  // Simple calculations - no memoization needed
  const showRing = showAccountColors && Boolean(accountColor);
  const ringColor = accountColor || '#2563eb';
  const isOperationPending = operationState === 'pending';
  const hasOperationError = operationState === 'error';
  
  // Use a subtle left border indicator instead of full background for unread
  // and a slightly stronger glass effect
  const itemStyle: CSSProperties = {};
  
  const subjectText = email.subject?.trim() || 'No subject';
  const rawSnippetText = email.snippet?.trim();
  const hasSnippetText = Boolean(rawSnippetText);
  const snippetText = hasSnippetText ? rawSnippetText : 'No preview available';
  const shouldAnimateEntry = index < MAX_STAGGERED_ANIMATION_ITEMS;
  const animationDelay = shouldAnimateEntry ? index * STAGGER_STEP_SECONDS : 0;

  const renderLabelChips = useCallback(
    (className?: string) => {
      if (!labelsVisible || !email.autoLabels.length) {
        return null;
      }
      return (
        <div className={cn('flex flex-wrap gap-1', className)}>
          {email.autoLabels.map((label) => (
            <AutoLabelBadge key={`${email.id}-${label.id}`} label={label} reason={label.reason} />
          ))}
        </div>
      );
    },
    [email.autoLabels, email.id, labelsVisible],
  );
  
  // Expensive: Array iteration
  const hasAttachments = useMemo(
    () =>
      email.attachments?.some(
        (attachment) =>
          attachment.validated !== false && !attachment.inline && !attachment.contentId,
      ) ?? false,
    [email.attachments]
  );
  const threadCountBadge = email.threadId && email.messageCount && email.messageCount > 1 ? (
    <Badge
      variant="outline"
      className="text-xs px-0 py-0 border-none text-foreground/60 flex items-center flex-shrink-0"
    >
      {email.messageCount}
    </Badge>
  ) : null;
  const draftBadge = email.draft ? (
    <Badge
      variant="secondary"
      className="text-[10px] uppercase tracking-wide bg-warning-surface text-warning-ink border border-warning-line flex-shrink-0"
    >
      Draft
    </Badge>
  ) : null;
  const contactApprovalState = email.contactApprovalState ?? null;
  const contactPending = contactApprovalState === 'pending';
  const contactBlocked = contactApprovalState === 'blocked';

  const contactStatusIcon =
    contactApprovalState && contactApprovalState !== 'approved'
      ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {contactApprovalState === 'blocked' ? (
                <Ban className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
              ) : (
                <Clock className="h-3.5 w-3.5 flex-shrink-0 text-warning-ink" />
              )}
            </TooltipTrigger>
            <TooltipContent>
              {contactApprovalState === 'blocked' ? 'Blocked sender' : 'Pending approval'}
            </TooltipContent>
          </Tooltip>
        )
      : null;
  const operationIndicator = isOperationPending ? (
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  ) : hasOperationError ? (
    <Badge variant="destructive" className="text-xs px-1 py-0">
      Error
    </Badge>
  ) : null;

  const handleDragStart = useCallback(
    (event: DragEvent) => {
      if (disableDrag) {
        return;
      }
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) {
        return;
      }
      dataTransfer.effectAllowed = 'move';
      dataTransfer.setData('application/email-id', email.id);
      dataTransfer.setData('text/plain', email.subject || email.id);
      
      // Create a clean custom drag preview
      createEmailDragPreview(email.subject || 'No subject', event);
      
      onEmailDragStart?.(email);
    },
    [disableDrag, email, onEmailDragStart],
  );

  const handleDragEnd = useCallback(() => {
    if (disableDrag) {
      return;
    }
    onEmailDragEnd?.();
  }, [disableDrag, onEmailDragEnd]);

  const motionRef = useRef<HTMLDivElement | null>(null);
  const handleNodeRef = useCallback((node: HTMLDivElement | null) => {
    motionRef.current = node;
  }, []);

  React.useEffect(() => {
    const element = motionRef.current;
    if (!element || disableDrag) {
      return;
    }
    element.addEventListener('dragstart', handleDragStart as EventListener);
    element.addEventListener('dragend', handleDragEnd as EventListener);
    return () => {
      element.removeEventListener('dragstart', handleDragStart as EventListener);
      element.removeEventListener('dragend', handleDragEnd as EventListener);
    };
  }, [handleDragStart, handleDragEnd, disableDrag]);

  // Touch at any width: the Duo's inner screen is md+ but still a finger.
  const isNarrow = useIsMobile();
  const isTouch = useIsTouch();
  const isMobile = isNarrow || isTouch;
  const handleClick = useCallback(() => {
    // In bulk mode on touch screens the whole row is the toggle target —
    // checkboxes are too small a tap target to be the only one.
    if (showCheckbox && isMobile) {
      onToggleBulkSelect(email.id);
      return;
    }
    onEmailSelect(email.id, email.folderId, email.accountId, email.threadId ?? null);
  }, [email, onEmailSelect, showCheckbox, isMobile, onToggleBulkSelect]);

  // P15 (PERF_AUDIT): a short-debounced body prefetch on hover. The delay filters
  // out fast scroll-scrubbing (only a deliberate hover warms the cache); the
  // store action dedupes in-flight requests and skips already-hydrated bodies, so
  // the actual open becomes a cheap 304 + optimistic header-with-body.
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseEnter = useCallback(() => {
    setHoveredEmail(email.id);
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
    }
    prefetchTimerRef.current = setTimeout(() => {
      void useEmailStore
        .getState()
        .prefetchEmail(
          email.id,
          email.accountId,
          email.folderId,
          email.threadId ?? null,
          email.messageCount ?? 1
        );
    }, 120);
  }, [email.id, email.accountId, email.folderId, email.threadId, email.messageCount, setHoveredEmail]);

  const handleMouseLeave = useCallback(() => {
    clearHover();
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }
  }, [clearHover]);

  // Review finding 8: a virtualized row can unmount mid-debounce (fast scroll)
  // without a mouseleave — clear the pending prefetch timer so it can't fire a
  // stray fetch after the row is gone.
  useEffect(() => {
    return () => {
      if (prefetchTimerRef.current) {
        clearTimeout(prefetchTimerRef.current);
      }
    };
  }, []);

  const showApproveAction = contactPending || contactBlocked;
  const showBlockAction = contactPending;
  const hasTrainingOverflowActions = Boolean(onTrainSpam || onTrainGood);
  const hasReplyActions = Boolean(onEmailReply || onEmailReplyAll || onEmailForward);

  // Swipe layout (iOS Mail convention): archive lives alone on the left rail
  // (full swipe right = archive); the right rail is read/unread, move, delete
  // with delete at the screen edge (full swipe left = delete). Approve/block
  // for pending senders stay reachable via the row context menu.
  const leftSwipeActions = useMemo<SwipeActionConfig[]>(
    () => [
      {
        key: 'archive',
        label: 'Archive',
        icon: <Archive className="h-5 w-5" />,
        tone: 'accent',
        disabled: isOperationPending,
        commitStyle: 'fly',
        onAction: () => {
          onEmailArchive(email.id);
        },
      },
    ],
    [email.id, isOperationPending, onEmailArchive],
  );

  const rightSwipeActions = useMemo<SwipeActionConfig[]>(
    () => [
      {
        key: email.read ? 'mark-unread' : 'mark-read',
        label: email.read ? 'Unread' : 'Read',
        icon: email.read ? <Mail className="h-5 w-5" /> : <MailOpen className="h-5 w-5" />,
        tone: 'primary',
        disabled: isOperationPending,
        onAction: () => {
          if (email.read) {
            onEmailMarkUnread(email.id);
          } else {
            onEmailMarkRead(email.id);
          }
        },
      },
      {
        key: 'move',
        label: 'Move',
        icon: <FolderInput className="h-5 w-5" />,
        tone: 'muted',
        disabled: isOperationPending,
        onAction: () => {
          useMoveToFolderStore.getState().open([email.id]);
        },
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 className="h-5 w-5" />,
        tone: 'destructive',
        disabled: isOperationPending,
        commitStyle: 'fly',
        onAction: () => {
          onEmailDelete(email.id);
        },
      },
    ],
    [email.id, email.read, isOperationPending, onEmailDelete, onEmailMarkRead, onEmailMarkUnread],
  );

  return (
    <SwipeableRow
      ref={swipeRowRef}
      className="group"
      contentClassName="rounded-xl"
      leftActions={leftSwipeActions}
      rightActions={rightSwipeActions}
      disabled={swipeDisabled}
    >
      <motion.div
        ref={handleNodeRef}
        initial={
          shouldAnimateEntry
            ? { opacity: 0, y: 20, scale: 0.95 }
            : { opacity: 1, y: 0, scale: 1 }
        }
        animate={
          draggingEmailId === email.id
            ? {
                opacity: 0.5,
                scale: 0.98,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              }
            : { opacity: 1, y: 0, scale: 1 }
        }
        exit={{ opacity: 0, x: -30, scale: 0.95 }}
        transition={{
          ...easings.springGentle,
          delay: animationDelay,
        }}
        whileHover={!disableDrag ? { y: -2, scale: 1.005 } : undefined}
        whileTap={!disableDrag ? { scale: 0.98 } : undefined}
        className={cn(
          'relative flex w-full gap-3 rounded-xl transition-all duration-200 box-border overflow-hidden glass-item',
          'items-stretch p-3 md:items-center md:py-2 md:px-4',
          !disableDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
          isSelected && 'ring-1 ring-primary/40 bg-primary/5',
          draggingEmailId === email.id && 'ring-2 ring-primary/60',
        )}
        style={itemStyle}
        draggable={!disableDrag}
        // Mouse only: hover state + prefetch mean nothing for a finger, and on
        // iOS a mouseenter handler that changes the page can swallow the tap.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            handleMouseEnter();
          }
        }}
        onPointerLeave={handleMouseLeave}
        {...longPressHandlers}
        // Anchor for keyboard shortcuts to resolve the row physically under the
        // cursor at keypress time (robust to scroll / stale hover) — audit §2.17 #5.
        data-email-row-id={email.id}
      >

        {showCheckbox && (
          <div className="flex items-center gap-3 pl-2">
            <Checkbox checked={isChecked} onCheckedChange={() => onToggleBulkSelect(email.id)} />
          </div>
        )}

        <div
          className="flex flex-1 gap-3 overflow-hidden items-center"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            if (swipeRowRef.current?.openSide()) {
              event.preventDefault();
              event.stopPropagation();
              swipeRowRef.current?.close();
              return;
            }
            handleClick();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (swipeRowRef.current?.openSide()) {
                event.preventDefault();
                swipeRowRef.current?.close();
                return;
              }
              event.preventDefault();
              onEmailSelect(email.id, email.folderId, email.accountId, email.threadId ?? null);
            }
          }}
        >
          <div className="relative flex-shrink-0">
            <RichTooltip delayDuration={200}>
              <RichTooltipTrigger asChild>
                <Avatar
                  className="h-10 w-10 m-1 flex-shrink-0"
                  style={showRing
                    ? {
                        boxShadow: `0 0 0 2px ${ringColor}`,
                      }
                    : undefined}
                >
                  {avatarSrc && (
                      <AvatarImage src={avatarSrc} alt={avatarAltLabel} />
                  )}
                  <AvatarFallback className="text-xs">
                    {getInitials((displayAddress?.name ?? email.from.name) ?? undefined, avatarEmail)}
                  </AvatarFallback>
                </Avatar>
              </RichTooltipTrigger>
              <RichTooltipContent side="top" align="start" className="w-80">
                <div className="space-y-2">
                  <div className="text-sm font-semibold leading-snug text-foreground">
                    {email.subject || 'No subject'}
                  </div>
                  {email.snippet && (
                    <p className="text-xs leading-relaxed text-muted-foreground/90">{email.snippet}</p>
                  )}
                  <div className="space-y-1 text-[11px] leading-relaxed text-foreground/80">
                    <div>
                      <span className="font-medium text-foreground/70">From:</span>{' '}
                      {formatAddressDisplay(email.from)}
                    </div>
                    {Array.isArray(email.to) && email.to.length > 0 && (
                      <div>
                        <span className="font-medium text-foreground/70">To:</span>{' '}
                        {formatAddressList(email.to)}
                      </div>
                    )}
                  </div>
                </div>
              </RichTooltipContent>
            </RichTooltip>
            {contactStatusIcon && (
              <div className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-background border border-border shadow-sm">
                {contactStatusIcon}
              </div>
            )}
            {/* Desktop selection entry: checkbox surfaces over the avatar on
                hover; clicking it starts bulk selection (no standing toggle). */}
            {!showCheckbox && (
              <div
                className="absolute inset-0 z-10 hidden items-center justify-center rounded-full bg-card/95 md:group-hover:flex"
                onClick={(event) => {
                  event.stopPropagation();
                  enterBulkSelect();
                }}
              >
                <Checkbox checked={isChecked} className="pointer-events-none" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1 overflow-hidden cursor-pointer md:flex md:items-center md:gap-3">
            {/* Desktop layout - hidden on mobile */}
            <div className="hidden md:flex items-center gap-2 flex-shrink-0 w-[150px] lg:w-[220px] lg:min-w-[200px] lg:max-w-[260px]">
              {!email.read && (
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: 'var(--brand-cherry)' }}
                />
              )}
              <span
                className={cn(
                  'min-w-0 truncate text-sm whitespace-nowrap',
                  !email.read && 'font-bold'
                )}
              >
                {displayLabel}
              </span>
              {threadCountBadge}
            </div>
            <div className="hidden md:flex min-w-0 flex-1 items-baseline gap-2">
              <span
                className={cn(
                  'min-w-0 truncate text-sm text-foreground whitespace-nowrap',
                  !email.read && 'font-bold'
                )}
              >
                {subjectText}
              </span>
              {draftBadge}
              {hasSnippetText && (
                <span
                  className="hidden text-xs text-muted-foreground whitespace-nowrap md:block md:min-w-0 md:flex-1 md:truncate"
                  title={snippetText}
                >
                  — {snippetText}
                </span>
              )}
              {renderLabelChips('hidden md:flex text-[10px] text-muted-foreground/80')}
            </div>
            <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
              {operationIndicator}
              {hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
              {trackingEnabled && (
                <TrackingIndicator
                  messageId={email.id}
                  summary={tracking?.summary ?? null}
                  events={tracking?.events ?? []}
                  isLoading={trackingLoading}
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="w-[68px] text-right">{formattedDate}</span>
                </TooltipTrigger>
                <TooltipContent side="top" align="end">
                  {fullDate}
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Mobile layout - hidden on desktop */}
            <div className="flex flex-col md:hidden">
              <div className="flex items-baseline justify-between gap-2 w-full">
                <div className="flex items-center gap-2 min-w-0">
                  {!email.read && (
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-full"
                      style={{ backgroundColor: 'var(--brand-cherry)' }}
                    />
                  )}
                  <span className={cn('truncate text-sm pb-0.5', !email.read && 'font-bold')}>
                    {displayLabel}
                  </span>
                  {threadCountBadge}
                </div>
                <div className="flex items-center gap-0.5">
                  {operationIndicator}
                  {hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
                  {trackingEnabled && (
                    <TrackingIndicator
                      messageId={email.id}
                      summary={tracking?.summary ?? null}
                      events={tracking?.events ?? []}
                      isLoading={trackingLoading}
                    />
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground w-[55px] text-right">
                        {formattedDate}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="end">
                      {fullDate}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="mt-2 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <p className={cn('truncate text-sm text-foreground', !email.read && 'font-bold')}>
                    {subjectText}
                  </p>
                  {draftBadge}
                </div>
                <p
                  className={cn(
                    'truncate text-xs',
                    hasSnippetText ? 'text-muted-foreground' : 'italic text-muted-foreground/70',
                    hasSnippetText && !email.read && 'font-bold',
                  )}
                >
                  {snippetText}
                </p>
                {renderLabelChips('mt-1 text-[10px] text-muted-foreground/80')}
              </div>
            </div>
            </div>
          </div>
        </motion.div>

      {/* Hover overlay anchored to right. Fixed slot layout (RowHoverActions)
          so archive/delete/etc sit at the same x-position on every row;
          per-row variable content (labels, approve/block) stays in `leading`. */}
      <RowHoverActions
        leading={
          (labelsVisible && email.autoLabels.length > 0) || showApproveAction || showBlockAction ? (
            <>
              {labelsVisible && email.autoLabels.length > 0 && (
                <div className="flex items-center gap-1 pointer-events-auto">
                  {email.autoLabels.map((label) => (
                    <AutoLabelBadge
                      key={`hover-${email.id}-${label.id}`}
                      label={label}
                      reason={label.reason}
                      showRemove={Boolean(onRemoveLabel && label.id)}
                      onRemove={label.id ? () => onRemoveLabel?.(email.id, label.id!) : undefined}
                    />
                  ))}
                </div>
              )}
              {showApproveAction && (
                <HoverActionButton
                  action={{
                    key: 'approve',
                    icon: <Check className="h-4 w-4" />,
                    label: 'Approve sender',
                    tone: 'success',
                    disabled: isOperationPending,
                    onAction: () => onEmailApproveSender(email.id),
                  }}
                />
              )}
              {showBlockAction && (
                <HoverActionButton
                  action={{
                    key: 'block',
                    icon: <Ban className="h-4 w-4" />,
                    label: 'Block sender',
                    tone: 'destructive',
                    disabled: isOperationPending,
                    onAction: () => onEmailBlockSender(email.id),
                  }}
                />
              )}
            </>
          ) : undefined
        }
        primary={[
          {
            key: 'archive',
            icon: <Archive className="h-4 w-4" />,
            label: 'Archive',
            disabled: isOperationPending,
            onAction: () => onEmailArchive(email.id),
          },
          {
            key: 'delete',
            icon: <Trash2 className="h-4 w-4" />,
            label: 'Delete',
            tone: 'destructive',
            disabled: isOperationPending,
            onAction: () => onEmailDelete(email.id),
          },
          {
            key: 'mark',
            icon: email.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />,
            label: email.read ? 'Mark as unread' : 'Mark as read',
            disabled: isOperationPending,
            onAction: () => {
              if (email.read) {
                onEmailMarkUnread(email.id);
              } else {
                onEmailMarkRead(email.id);
              }
            },
          },
        ]}
        middle={
          hasReplyActions
            ? [
                onEmailReply
                  ? {
                      key: 'reply',
                      icon: <Reply className="h-4 w-4" />,
                      label: 'Reply',
                      disabled: isOperationPending,
                      onAction: () => onEmailReply(email.id),
                    }
                  : null,
                onEmailReplyAll
                  ? {
                      key: 'reply-all',
                      icon: <ReplyAll className="h-4 w-4" />,
                      label: 'Reply all',
                      disabled: isOperationPending,
                      onAction: () => onEmailReplyAll(email.id),
                    }
                  : null,
                onEmailForward
                  ? {
                      key: 'forward',
                      icon: <Forward className="h-4 w-4" />,
                      label: 'Forward',
                      disabled: isOperationPending,
                      onAction: () => onEmailForward(email.id),
                    }
                  : null,
              ]
            : []
        }
        overflow={
          <>
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                useMoveToFolderStore.getState().open([email.id]);
              }}
              disabled={isOperationPending}
              className="gap-2"
            >
              <FolderInput className="h-4 w-4" />
              <span>Move to folder</span>
            </DropdownMenuItem>
            {onCreateTask && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onCreateTask(email.id, event.currentTarget as HTMLElement);
                }}
                disabled={isOperationPending}
                className="gap-2"
              >
                <CheckSquare className="h-4 w-4" />
                <span>Create task</span>
              </DropdownMenuItem>
            )}
            {onAddSenderLabel && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onAddSenderLabel(email.id);
                }}
                disabled={isOperationPending}
                className="gap-2"
              >
                <Tag className="h-4 w-4" />
                <span>Add sender label</span>
              </DropdownMenuItem>
            )}
            {email.from?.email && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  useSenderProfileStore.getState().openProfile(email.from.email);
                }}
                className="gap-2"
              >
                <UserRound className="h-4 w-4" />
                <span>Sender profile</span>
              </DropdownMenuItem>
            )}
            {hasTrainingOverflowActions && <DropdownMenuSeparator />}
            {onTrainSpam && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onTrainSpam(email.id);
                }}
                disabled={isOperationPending}
                className="gap-2"
              >
                <Flame className="h-4 w-4" />
                <span>Train as spam</span>
              </DropdownMenuItem>
            )}
            {onTrainGood && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onTrainGood(email.id);
                }}
                disabled={isOperationPending}
                className="gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                <span>Train as good</span>
              </DropdownMenuItem>
            )}
          </>
        }
        trailing={
          trackingEnabled ? (
            <TrackingIndicator
              messageId={email.id}
              summary={tracking?.summary ?? null}
              events={tracking?.events ?? []}
              isLoading={trackingLoading}
              className="pointer-events-auto"
            />
          ) : undefined
        }
        dateShort={formattedDate}
        dateFull={fullDate}
      />
    </SwipeableRow>
  );
},
(prevProps, nextProps) => {
  // Return true to skip re-render, false to re-render
  if (!prevProps.email || !nextProps.email) return false;
  
  // Core email properties that affect rendering
  if (prevProps.email.id !== nextProps.email.id) return false;
  if (prevProps.email.read !== nextProps.email.read) return false;
  if (prevProps.email.starred !== nextProps.email.starred) return false;
  if (prevProps.email.subject !== nextProps.email.subject) return false;
  if (prevProps.email.snippet !== nextProps.email.snippet) return false;
  if (prevProps.email.date !== nextProps.email.date) return false;

  // Sender/recipient information that affects display
  if (prevProps.email.from?.email !== nextProps.email.from?.email) return false;
  if (prevProps.email.from?.name !== nextProps.email.from?.name) return false;
  if (prevProps.email.to?.length !== nextProps.email.to?.length) return false;
  if (prevProps.email.to?.[0]?.email !== nextProps.email.to?.[0]?.email) return false;
  if (prevProps.email.to?.[0]?.name !== nextProps.email.to?.[0]?.name) return false;
  
  // UI state props
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isChecked !== nextProps.isChecked) return false;
  if (prevProps.showCheckbox !== nextProps.showCheckbox) return false;
  if (prevProps.operationState !== nextProps.operationState) return false;
  if (prevProps.onEmailReply !== nextProps.onEmailReply) return false;
  if (prevProps.onEmailReplyAll !== nextProps.onEmailReplyAll) return false;
  if (prevProps.onEmailForward !== nextProps.onEmailForward) return false;
  if (prevProps.onCreateTask !== nextProps.onCreateTask) return false;
  if (prevProps.onAddSenderLabel !== nextProps.onAddSenderLabel) return false;
  if (prevProps.onTrainSpam !== nextProps.onTrainSpam) return false;
  if (prevProps.onTrainGood !== nextProps.onTrainGood) return false;
  if (prevProps.onRemoveLabel !== nextProps.onRemoveLabel) return false;

  // Visual props
  if (prevProps.accountColor !== nextProps.accountColor) return false;
  if (prevProps.showAccountColors !== nextProps.showAccountColors) return false;
  if (prevProps.showRecipientNames !== nextProps.showRecipientNames) return false;
  if (prevProps.contactNameLookup !== nextProps.contactNameLookup) return false;
  if (prevProps.draggingEmailId !== nextProps.draggingEmailId) return false;
  
  // Email metadata that affects display
  if (prevProps.email.attachments?.length !== nextProps.email.attachments?.length) return false;
  if (prevProps.email.contactApprovalState !== nextProps.email.contactApprovalState) return false;
  if (prevProps.email.messageCount !== nextProps.email.messageCount) return false;
  if (prevProps.email.autoLabels?.length !== nextProps.email.autoLabels?.length) return false;

  // All props are equal - skip re-render
  return true;
});
